/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {
  CanColor,
  CanDisable,
  CanDisableRipple, MATERIAL_COMPATIBILITY_MODE,
  MdRipple,
  mixinColor,
  mixinDisabled,
  mixinDisableRipple,
  RippleRef,
} from '@angular/material/core';
import {FocusMonitor, FocusOrigin} from '@angular/cdk/a11y';


// Increasing integer for generating unique ids for checkbox components.
let /** @type {?} */ nextUniqueId = 0;
/**
 * Provider Expression that allows md-checkbox to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 * \@docs-private
 */
export const MD_CHECKBOX_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MdCheckbox),
  multi: true
};
export type TransitionCheckState = number;
export let TransitionCheckState: any = {};
TransitionCheckState.Init = 0;
TransitionCheckState.Checked = 1;
TransitionCheckState.Unchecked = 2;
TransitionCheckState.Indeterminate = 3;
TransitionCheckState[TransitionCheckState.Init] = "Init";
TransitionCheckState[TransitionCheckState.Checked] = "Checked";
TransitionCheckState[TransitionCheckState.Unchecked] = "Unchecked";
TransitionCheckState[TransitionCheckState.Indeterminate] = "Indeterminate";

/**
 * Change event object emitted by MdCheckbox.
 */
export class MdCheckboxChange {
/**
 * The source MdCheckbox of the event.
 */
source: MdCheckbox;
/**
 * The new `checked` value of the checkbox.
 */
checked: boolean;
}

function MdCheckboxChange_tsickle_Closure_declarations() {
/**
 * The source MdCheckbox of the event.
 * @type {?}
 */
MdCheckboxChange.prototype.source;
/**
 * The new `checked` value of the checkbox.
 * @type {?}
 */
MdCheckboxChange.prototype.checked;
}

/**
 * \@docs-private
 */
export class MdCheckboxBase {
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 */
constructor(public _renderer: Renderer2,
public _elementRef: ElementRef) {}
}

function MdCheckboxBase_tsickle_Closure_declarations() {
/** @type {?} */
MdCheckboxBase.prototype._renderer;
/** @type {?} */
MdCheckboxBase.prototype._elementRef;
}

export const /** @type {?} */ _MdCheckboxMixinBase =
  mixinColor(mixinDisableRipple(mixinDisabled(MdCheckboxBase)), 'accent');
/**
 * A material design checkbox component. Supports all of the functionality of an HTML5 checkbox,
 * and exposes a similar API. A MdCheckbox can be either checked, unchecked, indeterminate, or
 * disabled. Note that all additional accessibility attributes are taken care of by the component,
 * so there is no need to provide them yourself. However, if you want to omit a label and still
 * have the checkbox be accessible, you may supply an [aria-label] input.
 * See: https://www.google.com/design/spec/components/selection-controls.html
 */
export class MdCheckbox extends _MdCheckboxMixinBase implements ControlValueAccessor, AfterViewInit,
    OnDestroy, CanColor, CanDisable, CanDisableRipple {
/**
 * Attached to the aria-label attribute of the host element. In most cases, arial-labelledby will
 * take precedence so this may be omitted.
 */
ariaLabel: string = '';
/**
 * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
 */
ariaLabelledby: string | null = null;
private _uniqueId: string = `md-checkbox-${++nextUniqueId}`;
/**
 * A unique id for the checkbox input. If none is supplied, it will be auto-generated.
 */
id: string = this._uniqueId;
/**
 * Returns the unique id for the visual hidden input.
 * @return {?}
 */
get inputId(): string { return `${this.id || this._uniqueId}-input`; }
private _required: boolean;
/**
 * Whether the checkbox is required.
 * @return {?}
 */
get required(): boolean { return this._required; }
/**
 * @param {?} value
 * @return {?}
 */
set required(value) { this._required = coerceBooleanProperty(value); }
/**
 * Whether or not the checkbox should appear before or after the label.
 * @deprecated
 * @return {?}
 */
get align(): 'start' | 'end' {
    // align refers to the checkbox relative to the label, while labelPosition refers to the
    // label relative to the checkbox. As such, they are inverted.
    return this.labelPosition == 'after' ? 'start' : 'end';
  }
/**
 * @param {?} v
 * @return {?}
 */
set align(v) {
    this.labelPosition = (v == 'start') ? 'after' : 'before';
  }
/**
 * Whether the label should appear after or before the checkbox. Defaults to 'after'
 */
labelPosition: 'before' | 'after' = 'after';
/**
 * Tabindex value that is passed to the underlying input element.
 */
tabIndex: number = 0;
/**
 * Name value will be applied to the input element if present
 */
name: string | null = null;
/**
 * Event emitted when the checkbox's `checked` value changes.
 */
change: EventEmitter<MdCheckboxChange> = new EventEmitter<MdCheckboxChange>();
/**
 * Event emitted when the checkbox's `indeterminate` value changes.
 */
indeterminateChange: EventEmitter<boolean> = new EventEmitter<boolean>();
/**
 * The value attribute of the native input element
 */
value: string ;
/**
 * The native `<input type="checkbox"> element
 */
_inputElement: ElementRef;
/**
 * Called when the checkbox is blurred. Needed to properly implement ControlValueAccessor.
 */
_ripple: MdRipple;
/**
 * Called when the checkbox is blurred. Needed to properly implement ControlValueAccessor.
 * \@docs-private
 */
onTouched: () => any = () => {};
private _currentAnimationClass: string = '';
private _currentCheckState: TransitionCheckState = TransitionCheckState.Init;
private _checked: boolean = false;
private _indeterminate: boolean = false;
private _controlValueAccessorChangeFn: (value: any) => void = () => {};
/**
 * Reference to the focused state ripple.
 */
private _focusRipple: RippleRef | null;
/**
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} _changeDetectorRef
 * @param {?} _focusMonitor
 */
constructor(renderer: Renderer2,
              elementRef: ElementRef,
private _changeDetectorRef: ChangeDetectorRef,
private _focusMonitor: FocusMonitor) {
    super(renderer, elementRef);
  }
/**
 * @return {?}
 */
ngAfterViewInit() {
    this._focusMonitor
      .monitor(this._inputElement.nativeElement, this._renderer, false)
      .subscribe(focusOrigin => this._onInputFocusChange(focusOrigin));
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._focusMonitor.stopMonitoring(this._inputElement.nativeElement);
  }
/**
 * Whether the checkbox is checked.
 * @return {?}
 */
get checked() {
    return this._checked;
  }
/**
 * @param {?} checked
 * @return {?}
 */
set checked(checked: boolean) {
    if (checked != this.checked) {
      this._checked = checked;
      this._changeDetectorRef.markForCheck();
    }
  }
/**
 * Whether the checkbox is indeterminate. This is also known as "mixed" mode and can be used to
 * represent a checkbox with three states, e.g. a checkbox that represents a nested list of
 * checkable items. Note that whenever checkbox is manually clicked, indeterminate is immediately
 * set to false.
 * @return {?}
 */
get indeterminate() {
    return this._indeterminate;
  }
/**
 * @param {?} indeterminate
 * @return {?}
 */
set indeterminate(indeterminate: boolean) {
    let /** @type {?} */ changed =  indeterminate != this._indeterminate;
    this._indeterminate = indeterminate;

    if (changed) {
      if (this._indeterminate) {
        this._transitionCheckState(TransitionCheckState.Indeterminate);
      } else {
        this._transitionCheckState(
          this.checked ? TransitionCheckState.Checked : TransitionCheckState.Unchecked);
      }
      this.indeterminateChange.emit(this._indeterminate);
    }
  }
/**
 * @return {?}
 */
_isRippleDisabled() {
    return this.disableRipple || this.disabled;
  }
/**
 * Method being called whenever the label text changes.
 * @return {?}
 */
_onLabelTextChange() {
    // This method is getting called whenever the label of the checkbox changes.
    // Since the checkbox uses the OnPush strategy we need to notify it about the change
    // that has been recognized by the cdkObserveContent directive.
    this._changeDetectorRef.markForCheck();
  }
/**
 * Sets the model value. Implemented as part of ControlValueAccessor.
 * @param {?} value Value to be set to the model.
 * @return {?}
 */
writeValue(value: any) {
    this.checked = !!value;
  }
/**
 * Registers a callback to be triggered when the value has changed.
 * Implemented as part of ControlValueAccessor.
 * @param {?} fn Function to be called on change.
 * @return {?}
 */
registerOnChange(fn: (value: any) => void) {
    this._controlValueAccessorChangeFn = fn;
  }
/**
 * Registers a callback to be triggered when the control has been touched.
 * Implemented as part of ControlValueAccessor.
 * @param {?} fn Callback to be triggered when the checkbox is touched.
 * @return {?}
 */
registerOnTouched(fn: any) {
    this.onTouched = fn;
  }
/**
 * Sets the checkbox's disabled state. Implemented as a part of ControlValueAccessor.
 * @param {?} isDisabled Whether the checkbox should be disabled.
 * @return {?}
 */
setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this._changeDetectorRef.markForCheck();
  }
/**
 * @param {?} newState
 * @return {?}
 */
private _transitionCheckState(newState: TransitionCheckState) {
    let /** @type {?} */ oldState = this._currentCheckState;
    let /** @type {?} */ renderer = this._renderer;
    let /** @type {?} */ elementRef = this._elementRef;

    if (oldState === newState) {
      return;
    }
    if (this._currentAnimationClass.length > 0) {
      renderer.removeClass(elementRef.nativeElement, this._currentAnimationClass);
    }

    this._currentAnimationClass = this._getAnimationClassForCheckStateTransition(
        oldState, newState);
    this._currentCheckState = newState;

    if (this._currentAnimationClass.length > 0) {
      renderer.addClass(elementRef.nativeElement, this._currentAnimationClass);
    }
  }
/**
 * @return {?}
 */
private _emitChangeEvent() {
    let /** @type {?} */ event = new MdCheckboxChange();
    event.source = this;
    event.checked = this.checked;

    this._controlValueAccessorChangeFn(this.checked);
    this.change.emit(event);
  }
/**
 * Function is called whenever the focus changes for the input element.
 * @param {?} focusOrigin
 * @return {?}
 */
private _onInputFocusChange(focusOrigin: FocusOrigin) {
    if (!this._focusRipple && focusOrigin === 'keyboard') {
      this._focusRipple = this._ripple.launch(0, 0, {persistent: true, centered: true});
    } else if (!focusOrigin) {
      this._removeFocusRipple();
      this.onTouched();
    }
  }
/**
 * Toggles the `checked` state of the checkbox.
 * @return {?}
 */
toggle(): void {
    this.checked = !this.checked;
  }
/**
 * Event handler for checkbox input element.
 * Toggles checked state if element is not disabled.
 * Do not toggle on (change) event since IE doesn't fire change event when
 *   indeterminate checkbox is clicked.
 * @param {?} event
 * @return {?}
 */
_onInputClick(event: Event) {
    // We have to stop propagation for click events on the visual hidden input element.
    // By default, when a user clicks on a label element, a generated click event will be
    // dispatched on the associated input element. Since we are using a label element as our
    // root container, the click event on the `checkbox` will be executed twice.
    // The real click event will bubble up, and the generated click event also tries to bubble up.
    // This will lead to multiple click events.
    // Preventing bubbling for the second event will solve that issue.
    event.stopPropagation();

    this._removeFocusRipple();

    if (!this.disabled) {
      // When user manually click on the checkbox, `indeterminate` is set to false.
      if (this._indeterminate) {
        Promise.resolve().then(() => {
          this._indeterminate = false;
          this.indeterminateChange.emit(this._indeterminate);
        });
      }

      this.toggle();
      this._transitionCheckState(
        this._checked ? TransitionCheckState.Checked : TransitionCheckState.Unchecked);

      // Emit our custom change event if the native input emitted one.
      // It is important to only emit it, if the native input triggered one, because
      // we don't want to trigger a change event, when the `checked` variable changes for example.
      this._emitChangeEvent();
    }
  }
/**
 * Focuses the checkbox.
 * @return {?}
 */
focus(): void {
    this._focusMonitor.focusVia(this._inputElement.nativeElement, 'keyboard');
  }
/**
 * @param {?} event
 * @return {?}
 */
_onInteractionEvent(event: Event) {
    // We always have to stop propagation on the change event.
    // Otherwise the change event, from the input element, will bubble up and
    // emit its event object to the `change` output.
    event.stopPropagation();
  }
/**
 * @param {?} oldState
 * @param {?} newState
 * @return {?}
 */
private _getAnimationClassForCheckStateTransition(
      oldState: TransitionCheckState, newState: TransitionCheckState): string {
    let /** @type {?} */ animSuffix: string = '';

    switch (oldState) {
      case TransitionCheckState.Init:
        // Handle edge case where user interacts with checkbox that does not have [(ngModel)] or
        // [checked] bound to it.
        if (newState === TransitionCheckState.Checked) {
          animSuffix = 'unchecked-checked';
        } else if (newState == TransitionCheckState.Indeterminate) {
          animSuffix = 'unchecked-indeterminate';
        } else {
          return '';
        }
        break;
      case TransitionCheckState.Unchecked:
        animSuffix = newState === TransitionCheckState.Checked ?
            'unchecked-checked' : 'unchecked-indeterminate';
        break;
      case TransitionCheckState.Checked:
        animSuffix = newState === TransitionCheckState.Unchecked ?
            'checked-unchecked' : 'checked-indeterminate';
        break;
      case TransitionCheckState.Indeterminate:
        animSuffix = newState === TransitionCheckState.Checked ?
            'indeterminate-checked' : 'indeterminate-unchecked';
        break;
    }

    return `mat-checkbox-anim-${animSuffix}`;
  }
/**
 * Fades out the focus state ripple.
 * @return {?}
 */
private _removeFocusRipple(): void {
    if (this._focusRipple) {
      this._focusRipple.fadeOut();
      this._focusRipple = null;
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-checkbox, mat-checkbox',
  templateUrl: 'checkbox.html',
  styleUrls: ['checkbox.css'],
  host: {
    'class': 'mat-checkbox',
    '[id]': 'id',
    '[class.mat-checkbox-indeterminate]': 'indeterminate',
    '[class.mat-checkbox-checked]': 'checked',
    '[class.mat-checkbox-disabled]': 'disabled',
    '[class.mat-checkbox-label-before]': 'labelPosition == "before"',
  },
  providers: [MD_CHECKBOX_CONTROL_VALUE_ACCESSOR],
  viewProviders: [{provide: MATERIAL_COMPATIBILITY_MODE, useValue: true}],
  inputs: ['disabled', 'disableRipple', 'color'],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
{type: ElementRef, },
{type: ChangeDetectorRef, },
{type: FocusMonitor, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'ariaLabel': [{ type: Input, args: ['aria-label', ] },],
'ariaLabelledby': [{ type: Input, args: ['aria-labelledby', ] },],
'id': [{ type: Input },],
'required': [{ type: Input },],
'align': [{ type: Input },],
'labelPosition': [{ type: Input },],
'tabIndex': [{ type: Input },],
'name': [{ type: Input },],
'change': [{ type: Output },],
'indeterminateChange': [{ type: Output },],
'value': [{ type: Input },],
'_inputElement': [{ type: ViewChild, args: ['input', ] },],
'_ripple': [{ type: ViewChild, args: [MdRipple, ] },],
'checked': [{ type: Input },],
'indeterminate': [{ type: Input },],
};
}

function MdCheckbox_tsickle_Closure_declarations() {
/** @type {?} */
MdCheckbox.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdCheckbox.ctorParameters;
/** @type {?} */
MdCheckbox.propDecorators;
/**
 * Attached to the aria-label attribute of the host element. In most cases, arial-labelledby will
 * take precedence so this may be omitted.
 * @type {?}
 */
MdCheckbox.prototype.ariaLabel;
/**
 * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
 * @type {?}
 */
MdCheckbox.prototype.ariaLabelledby;
/** @type {?} */
MdCheckbox.prototype._uniqueId;
/**
 * A unique id for the checkbox input. If none is supplied, it will be auto-generated.
 * @type {?}
 */
MdCheckbox.prototype.id;
/** @type {?} */
MdCheckbox.prototype._required;
/**
 * Whether the label should appear after or before the checkbox. Defaults to 'after'
 * @type {?}
 */
MdCheckbox.prototype.labelPosition;
/**
 * Tabindex value that is passed to the underlying input element.
 * @type {?}
 */
MdCheckbox.prototype.tabIndex;
/**
 * Name value will be applied to the input element if present
 * @type {?}
 */
MdCheckbox.prototype.name;
/**
 * Event emitted when the checkbox's `checked` value changes.
 * @type {?}
 */
MdCheckbox.prototype.change;
/**
 * Event emitted when the checkbox's `indeterminate` value changes.
 * @type {?}
 */
MdCheckbox.prototype.indeterminateChange;
/**
 * The value attribute of the native input element
 * @type {?}
 */
MdCheckbox.prototype.value;
/**
 * The native `<input type="checkbox"> element
 * @type {?}
 */
MdCheckbox.prototype._inputElement;
/**
 * Called when the checkbox is blurred. Needed to properly implement ControlValueAccessor.
 * @type {?}
 */
MdCheckbox.prototype._ripple;
/**
 * Called when the checkbox is blurred. Needed to properly implement ControlValueAccessor.
 * \@docs-private
 * @type {?}
 */
MdCheckbox.prototype.onTouched;
/** @type {?} */
MdCheckbox.prototype._currentAnimationClass;
/** @type {?} */
MdCheckbox.prototype._currentCheckState;
/** @type {?} */
MdCheckbox.prototype._checked;
/** @type {?} */
MdCheckbox.prototype._indeterminate;
/** @type {?} */
MdCheckbox.prototype._controlValueAccessorChangeFn;
/**
 * Reference to the focused state ripple.
 * @type {?}
 */
MdCheckbox.prototype._focusRipple;
/** @type {?} */
MdCheckbox.prototype._changeDetectorRef;
/** @type {?} */
MdCheckbox.prototype._focusMonitor;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
